import * as React from 'react';

import { ListView } from '@longline/aqua-ui/controls/ListView';
import { Message } from '@longline/aqua-ui/containers/Message';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { CountryFormatter } from '@longline/aqua-ui/formatters/CountryFormatter';

import { ISystem } from '../../api/models/ISystem';
import { useSystemsContext } from './ISystemsContext';
import { ISystemFilter, SystemFilter, hasFilter } from "../../filters/SystemFilter";
import { Pane } from '../../ui/Pane';
import { Can } from '../../ui/Can';
import { SystemInfoBox } from './SystemInfoBox';

const SystemList = () => {
  const ctx = useSystemsContext();

  const [filter, setFilter] = React.useState<ISystemFilter>({});

  const renderNoData = () => {
    if(ctx.systemsReady == 'failed') {
      return (
        <Message.Error margin onRetry={ctx.loadSystems}>
          Systems failed to load.
        </Message.Error>
      );
    } else if (hasFilter(filter)) {
      return (
        <Message.NoFilteredData margin onClear={() => setFilter({})}>
          No systems found. Your query may be too restrictive.
        </Message.NoFilteredData>
      )
    } else {
      return (
        <Message.NoData margin>
          You do not have access to any systems.
        </Message.NoData>
      )
    }
  }

  const handleAdd = () => {
    ctx.setMode('add');
  }

  // Filter systems. This is memoized for speed.
  const filteredSystems = React.useMemo(() => {
    return ctx.systems
    .filter(c => c.name.toLocaleLowerCase().includes((filter.q || "").toLocaleLowerCase()))
    .filter(c => !filter.country || (c.country == filter.country && (!filter.division || c.division == filter.division)));
  }, [ctx.systems, filter]);    

  return (
    <>
      <Pane.Heading>
        <SystemFilter filter={filter} onChange={setFilter}/>
      </Pane.Heading>
      <Pane.Subheader
        expandable
        action={<Can permission="manage_systems">
          <SecondaryButton title="Add system" positive icon={SVG.Icons.Plus} onClick={handleAdd} />
        </Can>}
      >
        Systems
      </Pane.Subheader>
      
      <ListView 
        dark 
        data={filteredSystems} 
        active={ctx.system}
        onClick={(system) => ctx.setSystem(system)}
        shadow
        aux={<SystemInfoBox onClose={() => ctx.setSystem(null)}/>}
        total
        hover
        singular="system"
        plural="systems"
        noData={renderNoData()}
        rowHeight={56}
        ghost={ctx.systemsReady == 'loading'}
        virtual
      >

        <ListView.Column paddingLeft={32} active width={3} name="Name" sort="name" defaultSort>
          {(item: ISystem) => item.name}
        </ListView.Column>

        <ListView.Column active width="120px" name="Country" sort="country" align="right">
          {(item: ISystem) => <CountryFormatter type="flag" value={item.country ?? ""}/>}
        </ListView.Column>

      </ListView>
    </>
  )
}

export { SystemList }
